// Producer types for Supabase integration

export type ProducerStatus = 'draft' | 'public';
export type ProducerBusinessStatus = 'pending' | 'active' | 'paused' | 'banned';
export type VerificationStatus = 'not_submitted' | 'pending' | 'verified';

export interface Producer {
  id: string;
  name: string;
  slug: string | null;
  short_description: string | null;
  description: string | null;
  address_text: string | null;
  city: string | null;
  region: string | null;
  county: string | null;
  lat: number | null;
  lng: number | null;
  categories: string[];
  phone: string | null;
  website: string | null;
  instagram: string | null;
  email: string | null;
  opening_hours_text: string | null;
  status: ProducerStatus;
  verified: boolean;
  verified_at: string | null;
  verification_note: string | null;
  producer_status: ProducerBusinessStatus;
  verification_submitted_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Payload for creating a producer (omit auto-generated fields + producer_status has DB default)
export type ProducerInsert = Omit<
  Producer,
  'id' | 'slug' | 'created_by' | 'created_at' | 'updated_at' | 'producer_status' | 'verification_submitted_at' | 'verified_at' | 'verification_note'
> & { producer_status?: ProducerBusinessStatus; verification_submitted_at?: string | null; verified_at?: string | null; verification_note?: string | null };

// Payload for updating a producer
export type ProducerUpdate = Partial<ProducerInsert>;

// Admin-friendly producer type for UI (camelCase)
export interface AdminProducer {
  id: string;
  name: string;
  slug: string | null;
  shortDescription?: string;
  longDescription?: string;
  address: string;
  city: string;
  region: string;
  lat: number;
  lng: number;
  categories: string[];
  phone?: string;
  website?: string;
  instagram?: string;
  email?: string;
  openingHoursText?: string;
  coverUrl?: string;
  galleryUrls: string[];
  status: ProducerStatus;
  verified: boolean;
  verifiedAt: string | null;
  verificationNote: string | null;
  producerStatus: ProducerBusinessStatus;
  verificationSubmittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Derive verification status from producer data
 */
export function getVerificationStatus(p: { verified: boolean; verification_submitted_at: string | null }): VerificationStatus {
  if (p.verified) return 'verified';
  if (p.verification_submitted_at) return 'pending';
  return 'not_submitted';
}

// Convert DB producer to admin-friendly format
export function toAdminProducer(p: Producer): AdminProducer {
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    shortDescription: p.short_description || '',
    longDescription: p.description || undefined,
    address: p.address_text || '',
    city: p.city || '',
    region: p.region || '',
    lat: p.lat || 0,
    lng: p.lng || 0,
    categories: p.categories || [],
    phone: p.phone || undefined,
    website: p.website || undefined,
    instagram: p.instagram || undefined,
    email: p.email || undefined,
    openingHoursText: p.opening_hours_text || undefined,
    coverUrl: undefined, // Resolved from media_assets
    galleryUrls: [], // Resolved from media_assets
    status: p.status,
    verified: p.verified,
    verifiedAt: p.verified_at ?? null,
    verificationNote: p.verification_note ?? null,
    producerStatus: p.producer_status || 'pending',
    verificationSubmittedAt: p.verification_submitted_at,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  };
}

// Convert admin-friendly producer to DB format for insert/update
export function toProducerPayload(admin: Omit<AdminProducer, 'id' | 'slug' | 'createdAt' | 'updatedAt'>): ProducerInsert {
  return {
    name: admin.name,
    short_description: admin.shortDescription || null,
    description: admin.longDescription || null,
    address_text: admin.address || null,
    city: admin.city || null,
    region: admin.region || null,
    county: null,
    lat: admin.lat || null,
    lng: admin.lng || null,
    categories: admin.categories || [],
    phone: admin.phone || null,
    website: admin.website || null,
    instagram: admin.instagram || null,
    email: admin.email || null,
    opening_hours_text: admin.openingHoursText || null,
    status: admin.status,
    verified: admin.verified,
    verified_at: admin.verifiedAt ?? null,
    verification_note: admin.verificationNote ?? null,
  };
}
